import Disclaimer from "@components/Disclaimer"
import Features from "@components/Features"
import FrequentlyAskedQuestions from "@components/FrequentlyAskedQuestions"
import Layout from "@components/Layout"
import LimitedTimeOfferBanner from "@components/LimitedTimeOfferBanner"
import LimitedTimeOfferDialog from "@components/LimitedTimeOfferDialog"
import MoneyBackGuarantee from "@components/MoneyBackGuarantee"
import Plans from "@components/Plans"
import Trust from "@components/Trust"
import {
  getOnboardingContext,
  getTryFirstFlow,
  isHomeFlow,
} from "@helpers/localStorage"
import useCountdownTimer from "@hooks/useCountdownTimer"
import { GetPricesData, GetPricesLoaderResult } from "@loaders/getPricesLoader"
import ErrorPage from "@pages/ErrorPage"
import LoadingPage from "@pages/LoadingPage"
import { Suspense, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { Await, useLoaderData } from "react-router-dom"
import { Plan } from "src/types"

const COUNTDOWN_START_MINUTES = 9
const DIALOG_DELAY_MS = 30000

export default function PlansPage() {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const context = getOnboardingContext()
  const [selectedPlan, setSelectedPlan] = useState<Plan>("quarterly")

  const data = useLoaderData() as GetPricesLoaderResult
  const { t } = useTranslation()
  const isHome = isHomeFlow()
  const plansCopyKey =
    context === "cars"
      ? "pages.checkout.plansCars"
      : context === "pets"
        ? "pages.checkout.plansPets"
        : isHome
          ? "pages.checkout.plansHome"
          : "pages.checkout.plans"
  const timeLeft = useCountdownTimer({ startMinutes: COUNTDOWN_START_MINUTES })
  const flow = useMemo(() => getTryFirstFlow(), [])

  const isDefaultContext = context !== "cars" && context !== "pets" && !isHome

  const getFlowLabel = () => {
    switch (flow) {
      case "hairstyle":
        return t("pages.checkout.plansV2.flowLabels.hairstyle")
      case "professional":
        return t("pages.checkout.plansV2.flowLabels.professional")
      case "looksmaxxing":
        return t("pages.checkout.plansV2.flowLabels.faceAnalysis")
      case "creative":
        return t("pages.checkout.plansV2.flowLabels.creative")
      case "celebrity":
        return t("pages.checkout.plansV2.flowLabels.celebrity")
      default:
        return t("pages.checkout.plansV2.flowLabels.style")
    }
  }

  useEffect(() => {
    const dialogTimeout = setTimeout(() => {
      setIsDialogOpen(true)
    }, DIALOG_DELAY_MS)

    return () => clearTimeout(dialogTimeout)
  }, [])

  if (isSubmitting) return <LoadingPage />

  const getPrices = (pricesData: GetPricesData) => {
    switch (selectedPlan) {
      case "weekly":
        return {
          fullPrice: pricesData.weeklyPrice,
          introPrice: pricesData.weeklyIntroPrice,
        }
      case "monthly":
        return {
          fullPrice: pricesData.monthlyPrice,
          introPrice: pricesData.monthlyIntroPrice,
        }
      case "quarterly":
        return {
          fullPrice: pricesData.quarterlyPrice,
          introPrice: pricesData.quarterlyIntroPrice,
        }
      case "yearly":
        return {
          fullPrice: pricesData.yearlyPrice,
          introPrice: pricesData.yearlyIntroPrice,
        }
      case "one-time":
        return {
          fullPrice: pricesData.oneTimePrice,
          introPrice: pricesData.oneTimePrice,
        }
    }
  }

  return (
    <Suspense fallback={<LoadingPage />}>
      <Await errorElement={<ErrorPage />} resolve={data.getPricesData}>
        {(getPricesData: GetPricesData) => {
          const { fullPrice, introPrice } = getPrices(getPricesData)

          return (
            <div>
              {timeLeft !== "00:00" && (
                <LimitedTimeOfferBanner
                  currency={getPricesData.currency}
                  fullPrice={fullPrice}
                  isSubmitting={isSubmitting}
                  plan={selectedPlan}
                  priceId={introPrice?.id}
                  setIsSubmitting={setIsSubmitting}
                  timeLeft={timeLeft}
                />
              )}

              <Layout as="div" className="min-h-0 gap-8">
                {isDefaultContext ? (
                  <div className="space-y-3 text-center">
                    <h1 className="heading">
                      {t("pages.checkout.plansV2.heading", {
                        flow: getFlowLabel(),
                      })}
                    </h1>
                    <p className="text-text-secondary text-base">
                      {t("pages.checkout.plansV2.subheading")}
                    </p>
                  </div>
                ) : (
                  <h1 className="heading">{t(`${plansCopyKey}.heading1`)}</h1>
                )}

                {isDefaultContext && (
                  <div className="flex items-center justify-center gap-6 text-center">
                    <div className="space-y-1">
                      <p className="text-2xl font-bold">300k+</p>
                      <p className="text-text-secondary text-xs">
                        {t("pages.checkout.plansV2.socialProof.usersText")}
                      </p>
                    </div>
                    <div className="bg-border h-10 w-px" />
                    <div className="space-y-1">
                      <p className="text-2xl font-bold">4M+</p>
                      <p className="text-text-secondary text-xs">
                        {t("pages.checkout.plansV2.socialProof.photosText")}
                      </p>
                    </div>
                    <div className="bg-border h-10 w-px" />
                    <div className="space-y-1">
                      <p className="text-2xl font-bold">4.5</p>
                      <p className="text-text-secondary text-xs">
                        {t("pages.checkout.plansV2.socialProof.ratingText")}
                      </p>
                    </div>
                  </div>
                )}

                <Plans
                  currency={getPricesData.currency}
                  isSubmitting={isSubmitting}
                  setIsSubmitting={setIsSubmitting}
                  timeLeft={timeLeft}
                  monthlyIntroPrice={getPricesData.monthlyIntroPrice}
                  monthlyPrice={getPricesData.monthlyPrice}
                  quarterlyIntroPrice={getPricesData.quarterlyIntroPrice}
                  quarterlyPrice={getPricesData.quarterlyPrice}
                  yearlyIntroPrice={getPricesData.yearlyIntroPrice}
                  yearlyPrice={getPricesData.yearlyPrice}
                  selectedPlan={selectedPlan}
                  onPlanChange={setSelectedPlan}
                />

                <Disclaimer
                  plan={selectedPlan}
                  fullPrice={fullPrice}
                  currency={getPricesData.currency}
                />

                {isDefaultContext && (
                  <div className="bg-surface-secondary rounded-2xl p-5 text-center">
                    <p className="text-text-secondary text-sm italic">
                      {t("pages.checkout.plansV2.testimonialHighlight")}
                    </p>
                  </div>
                )}

                <Trust />

                <Features />

                <MoneyBackGuarantee />

                <FrequentlyAskedQuestions />

                <Plans
                  currency={getPricesData.currency}
                  isSubmitting={isSubmitting}
                  setIsSubmitting={setIsSubmitting}
                  timeLeft={timeLeft}
                  monthlyIntroPrice={getPricesData.monthlyIntroPrice}
                  monthlyPrice={getPricesData.monthlyPrice}
                  quarterlyIntroPrice={getPricesData.quarterlyIntroPrice}
                  quarterlyPrice={getPricesData.quarterlyPrice}
                  yearlyIntroPrice={getPricesData.yearlyIntroPrice}
                  yearlyPrice={getPricesData.yearlyPrice}
                  selectedPlan={selectedPlan}
                  onPlanChange={setSelectedPlan}
                />

                <Disclaimer
                  plan={selectedPlan}
                  fullPrice={fullPrice}
                  currency={getPricesData.currency}
                />
              </Layout>

              <LimitedTimeOfferDialog
                currency={getPricesData.currency}
                isSubmitting={isSubmitting}
                onClose={() => setIsDialogOpen(false)}
                open={isDialogOpen}
                plan={selectedPlan}
                setIsSubmitting={setIsSubmitting}
                stripeIntroPrice={introPrice}
                stripePrice={fullPrice}
              />
            </div>
          )
        }}
      </Await>
    </Suspense>
  )
}
